import { useState, useEffect, useCallback } from "react";
import "../../../assets/css/reservation.css";
import { useNavigate } from "react-router-dom";
import { ReservationsController } from "../../../controllers/reservationsController";
import { ApiUtil } from "../../../util/apiUtil";
import { Button } from "react-bootstrap";
import { Loading } from "../../../components/loading";
import { TableName } from "../../../constants/tableName";
import { TableListStatus } from "../../../components/tableListStatus";
import { ReservationPreview } from "./reservationPreview";
import { UsersController } from "../../../controllers/usersController";
import { Dropdown } from "../../../components/dropdown";

export const AllReservationsList = ({
  isOpen = false,
  reservationSettingsId,
  reservationType = TableName.NORMALRESERVATIONS,
  backFunction = () => { },
  minDate,
  maxDate,
}) => {
  const navigate = useNavigate();
  const [user] = useState(() => JSON.parse(localStorage.getItem("user")));

  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  const [normalReservations, setNormalReservations] = useState([]);
  const [eventReservations, setEventReservations] = useState([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [reservationDate, setReservationDate] = useState("");
  const [filter, showFilter] = useState(false);

  const handlePopupDialogOpen = (data) => {
    setSelectedData(data);
    setIsPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setIsPreviewOpen(false);
  };

  const getAllNormalReservations = () => {
    return new Promise((resolve, reject) => {
      ReservationsController.getAll({
        type: TableName.NORMALRESERVATIONS,
        reservationSettingsId: reservationSettingsId,
        resolve: resolve,
        reject: reject,
      });
    });
  };

  const getAllEventReservations = () => {
    return new Promise((resolve, reject) => {
      ReservationsController.getAll({
        type: TableName.EVENTRESERVATIONS,
        reservationSettingsId: reservationSettingsId,
        resolve: resolve,
        reject: reject,
      });
    });
  };

  const getAllReservations = () => {
    if (!reservationSettingsId) return;

    setLoading(true);
    setProgress(0);

    Promise.all([getAllNormalReservations(), getAllEventReservations()])
      .then(async ([normalReservationsData, eventReservationsData]) => {
        let normalData = ApiUtil.deserialize({ data: normalReservationsData });
        let eventData = ApiUtil.deserialize({ data: eventReservationsData });

        // FIX: Safely parse date strings to Date objects for accurate comparison
        if (minDate && maxDate) {
          const minDateValue = new Date(minDate).setHours(0, 0, 0, 0);
          const maxDateValue = new Date(maxDate).setHours(23, 59, 59, 999); // Cover the entire end day

          const isWithinDates = (res) => {
            if (!res.reservationDate) return false;
            const resDateValue = new Date(res.reservationDate).setHours(
              0,
              0,
              0,
              0,
            );
            return resDateValue >= minDateValue && resDateValue <= maxDateValue;
          };

          normalData = normalData.filter(isWithinDates);
          eventData = eventData.filter(isWithinDates);
        }

        const totalRecords = normalData.length + eventData.length;
        if (totalRecords === 0) {
          setNormalReservations([]);
          setEventReservations([]);
          setProgress(100);
          setLoading(false);
          return;
        }

        let processedCount = 0;
        const updateProgress = () => {
          processedCount++;
          setProgress(Math.round((processedCount / totalRecords) * 100));
        };

        // Cache user promises so we don't query Firestore multiple times for the same user
        const userCache = {};

        const processDataInParallel = async (dataList) => {
          const results = [];
          const CHUNK_SIZE = 15; // Limit concurrent Firebase requests to prevent resource-exhaustion

          for (let i = 0; i < dataList.length; i += CHUNK_SIZE) {
            const chunk = dataList.slice(i, i + CHUNK_SIZE);

            const chunkPromises = chunk.map(async (data) => {
              const baseData = {
                ...data,
                slotTitle: data.timeSlot?.slotTitle || "",
                timeSlot: data.timeSlot
                  ? `${data.timeSlot.startTime} - ${data.timeSlot.endTime}`
                  : "",
                reservationDate: data.reservationDate,
                _rawStartTime: data.timeSlot?.startTime || "24:00",
              };

              try {
                let userDetail = null;
                if (data.userId) {
                  if (!userCache[data.userId]) {
                    // Save the promise to the cache
                    userCache[data.userId] = getUserDetail(data).then(
                      (rawUser) => ApiUtil.deserialize({ data: rawUser }),
                    );
                  }
                  userDetail = await userCache[data.userId];
                }

                updateProgress();
                return {
                  ...baseData,
                  fullName:
                    `${userDetail?.firstName || ""} ${userDetail?.lastName || ""}`.trim() ||
                    "Unknown",
                  contactNum: userDetail?.contactNum || "N/A",
                };
              } catch (err) {
                updateProgress();
                return { ...baseData, fullName: "Unknown", contactNum: "N/A" };
              }
            });

            // Wait for the current chunk of requests to finish before proceeding to the next
            results.push(...(await Promise.all(chunkPromises)));
          }
          return results;
        };

        const updatedNormalData = await processDataInParallel(normalData);
        const updatedEventData = await processDataInParallel(eventData);

        const sortAscending = (a, b) => {
          const dateA = a.reservationDate || "9999-12-31";
          const dateB = b.reservationDate || "9999-12-31";
          const dateComparison = dateA.localeCompare(dateB);
          if (dateComparison !== 0) return dateComparison;
          return a._rawStartTime.localeCompare(b._rawStartTime);
        };

        updatedNormalData.sort(sortAscending);
        updatedEventData.sort(sortAscending);

        setNormalReservations(updatedNormalData);
        setEventReservations(updatedEventData);
      })
      .catch((error) => console.debug("Error fetching reservations:", error))
      .finally(() => {
        setLoading(false);
      });
  };

  const getUserDetail = (reservations) => {
    return new Promise((resolve, reject) => {
      if (!reservations.userId) return resolve(null);
      UsersController.get({
        userId: reservations.userId,
        resolve,
        reject,
      });
    });
  };

  const handleOnChange = useCallback((value) => {
    setReservationDate(value);
  }, []);

  const generateDateOptions = (startDate, endDate) => {
    const options = [];
    if (!startDate || !endDate) return options;

    const currentDate = new Date(startDate);
    const end = new Date(endDate);
    while (currentDate <= end) {
      const dateString = currentDate.toISOString().split("T")[0];
      options.push({ value: dateString, label: dateString });
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return options;
  };

  const filteredReservations =
    reservationType === TableName.NORMALRESERVATIONS
      ? normalReservations.filter(
        (reservation) =>
          !reservationDate || reservation.reservationDate === reservationDate,
      )
      : eventReservations.filter(
        (reservation) =>
          !reservationDate || reservation.reservationDate === reservationDate,
      );

  useEffect(() => {
    if (reservationSettingsId) {
      getAllReservations();
    }
  }, [reservationSettingsId]);

  useEffect(() => {
    if (!user) navigate("/loginSignUp", { state: { isLoginToggle: true } });
  }, [user]);

  return loading ? (
    <div style={{ height: "60vh" }}>
      <Loading progress={progress} />
    </div>
  ) : (
    <section id="allReservationsList">
      <div className="reservation-container">
        <div>
          <h3>{`ALL ${reservationType === TableName.NORMALRESERVATIONS
              ? "NORMAL"
              : "EVENT"
            } RESERVATIONS LIST`}</h3>
          <hr />
          <div className="web-details">
            <Button onClick={() => showFilter(!filter)}>
              {filter ? "HIDE" : "FILTER"}
            </Button>
          </div>
          <form className="margin-bottom">
            {filter && (
              <Dropdown
                name="reservationDate"
                title="Reservation Date"
                value={reservationDate}
                options={generateDateOptions(minDate, maxDate)}
                isClearable={true}
                onChangeFunc={handleOnChange}
                isMandatory={false}
                placeholder="All"
              />
            )}
            <TableListStatus
              list={filteredReservations}
              type={reservationType}
              isFilter={filter}
              handlePopupDialogOpen={handlePopupDialogOpen}
            />
          </form>
        </div>
        <ReservationPreview
          user={user}
          type={reservationType}
          isOpen={isPreviewOpen}
          handleClose={handlePreviewClose}
          getAll={() => getAllReservations(reservationSettingsId)}
          selectedData={selectedData}
        />
      </div>
      <div className="button-container stick-bottom">
        <Button onClick={backFunction}>BACK</Button>
        <Button
          onClick={() => getAllReservations(reservationSettingsId)}
          className="arrow-button"
        >
          REFRESH
        </Button>
      </div>
    </section>
  );
};
